import mockExchangeData from './mockExchange.json';
import mockSecurityTypeData from './mockSecurityType.json';

/**
 * ScanResultController - Functional module for combining scan criteria data
 *
 * Merges data from different scan types (Exchange, SecurityType, etc.)
 * and returns a consistent format for the ScanResultsTable component.
 */

// Data registry - can be extended with new scan types. For now we will mock
// data imports from JSON files.
// TODO: Replace with real data fetching logic as needed.
const mockDataRegistry = {
  exchange: mockExchangeData,
  securityType: mockSecurityTypeData
};

/**
 * Combines scan results from multiple criteria into a single dataset
 *
 * @param {Array} scanTypes - Array of scan types to include ['exchange', 'securityType']
 * @param {number} maxResults - Maximum number of results to return (default: 100)
 * @returns {Array} Combined scan results with merged displayValues
 */

// Should we return all results to the table by default? and let the table
// paginate? For now, we limit to 100 here.

const MAX_RESULTS_DEFAULT = 100;

export const getCombinedScanResults = (
  scanTypes = ['exchange', 'securityType'],
  maxResults = MAX_RESULTS_DEFAULT
) => {
  const combinedResults = new Map();

  // Process each scan type
  scanTypes.forEach((scanType) => {
    const data = mockDataRegistry[scanType];
    if (!data?.data?.symbols) {
      console.warn(`No data found for scan type: ${scanType}`);
      return;
    }

    const symbols = data.data.symbols;

    // Merge each symbol's data
    Object.keys(symbols).forEach((symbolKey) => {
      const symbolData = symbols[symbolKey];

      if (combinedResults.has(symbolKey)) {
        // Merge with existing symbol data
        const existingData = combinedResults.get(symbolKey);

        // Merge individual scan type values as separate properties
        const updatedData = { ...existingData };

        // Add each displayValue as a separate property
        Object.keys(symbolData.displayValues).forEach((key) => {
          updatedData[key] = symbolData.displayValues[key];
        });

        // Combine filterValues (comma-separated)
        const existingFilterValue = existingData.filterValue || '';
        const newFilterValue = symbolData.filterValue || '';
        const combinedFilterValue = [existingFilterValue, newFilterValue]
          .filter(Boolean)
          .join(',');

        updatedData.filterValue = combinedFilterValue;

        combinedResults.set(symbolKey, updatedData);
      } else {
        // Create new symbol entry with individual properties
        const newEntry = {
          symbol: symbolKey,
          filterValue: symbolData.filterValue || ''
        };

        // Add each displayValue as a separate property
        Object.keys(symbolData.displayValues).forEach((key) => {
          newEntry[key] = symbolData.displayValues[key];
        });

        combinedResults.set(symbolKey, newEntry);
      }
    });
  });

  // Convert map to array, remove filterValue from table output, and limit results
  return Array.from(combinedResults.values())
    .map((result) => {
      // eslint-disable-next-line no-unused-vars
      const { filterValue, ...tableData } = result;
      return tableData;
    })
    .slice(0, maxResults);
};

/**
 * Gets scan results for a specific scan type only
 *
 * @param {string} scanType - The scan type ('exchange', 'securityType')
 * @param {number} maxResults - Maximum number of results to return
 * @returns {Array} Scan results for the specified type
 */
export const getScanResults = (scanType, maxResults = MAX_RESULTS_DEFAULT) => {
  const data = mockDataRegistry[scanType];
  if (!data?.data?.symbols) {
    console.warn(`No data found for scan type: ${scanType}`);
    return [];
  }

  const symbols = data.data.symbols;

  return Object.keys(symbols)
    .slice(0, maxResults)
    .map((symbolKey) => ({
      symbol: symbolKey,
      filterValue: symbols[symbolKey].filterValue || '',
      displayValues: {
        ...symbols[symbolKey].displayValues
      }
    }));
};

/**
 * Gets available scan types
 *
 * @returns {Array} Array of available scan type names
 */
export const getAvailableScanTypes = () => {
  return Object.keys(mockDataRegistry);
};

/**
 * Adds a new mock data source
 *
 * @param {string} scanType - Name of the scan type
 * @param {Object} data - Mock data in the standard format
 */
export const addMockData = (scanType, data) => {
  mockDataRegistry[scanType] = data;
};

// Default export object for backwards compatibility
const scanResultController = {
  getCombinedScanResults,
  getScanResults,
  getAvailableScanTypes,
  addMockData
};

export default scanResultController;
